import React, { useState, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import api from '../../../../../services/api';
import NavBarGestao from '../../../../../components/NavBarGestao';

// Material UI Components
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tooltip,
    InputAdornment,
    Snackbar
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

// Icons
import CallIcon from '@material-ui/icons/Call';
import AddIcCallIcon from '@material-ui/icons/AddIcCall';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import PersonIcon from '@material-ui/icons/Person';
import SearchIcon from '@material-ui/icons/Search';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import BusinessIcon from '@material-ui/icons/Business';
import RoomIcon from '@material-ui/icons/Room';
import ContactPhoneIcon from '@material-ui/icons/ContactPhone';
import DevicesIcon from '@material-ui/icons/Devices';

export default function Telefonia() {
    // Tema Dark Mode
    const [darkMode, setDarkMode] = useState(() => {
        const saved = Cookies.get('darkMode') || Cookies.get('tema') || Cookies.get('theme');
        return saved === 'true' || saved === 'dark';
    });

    useEffect(() => {
        const updateTheme = () => {
            const saved = Cookies.get('darkMode') || Cookies.get('tema') || Cookies.get('theme');
            setDarkMode(saved === 'true' || saved === 'dark');
        };
        window.addEventListener('darkModeChanged', updateTheme);
        return () => window.removeEventListener('darkModeChanged', updateTheme);
    }, []);

    // Estados de dados
    const [ramais, setRamais] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Paginação
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Modal Novo Ramal
    const [openModal, setOpenModal] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [novoRamal, setNovoRamal] = useState('');
    const [novoNomeMapa, setNovoNomeMapa] = useState('');
    const [novaLocalizacao, setNovaLocalizacao] = useState('');
    const [idUsuarioSelecionado, setIdUsuarioSelecionado] = useState('');

    // Modal Excluir
    const [itemExcluir, setItemExcluir] = useState(null);
    const [openModalExcluir, setOpenModalExcluir] = useState(false);
    const [excluindo, setExcluindo] = useState(false);

    // Feedback SnackBar
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    const mostrarToast = (message, severity = 'info') => {
        let texto = 'Operação realizada com sucesso!';
        if (typeof message === 'string') {
            texto = message;
        } else if (message && typeof message === 'object') {
            texto = (message.message && typeof message.message === 'string')
                ? message.message
                : (severity === 'error' ? 'Ocorreu um erro na operação.' : 'Ramal salvo com sucesso!');
        }
        setToast({ open: true, message: String(texto), severity });
    };

    const fecharToast = () => {
        setToast(prev => ({ ...prev, open: false }));
    };

    // Buscar lista de ramais
    const carregarRamais = async () => {
        try {
            setLoading(true);
            const headers = {
                'Authorization': 'Bearer ' + Cookies.get("token"),
                'Content-Type': 'application/json'
            };
            const response = await api.get('/v1/protected/equipamentos/ramais', { headers });
            if (response.data && Array.isArray(response.data)) {
                setRamais(response.data);
            } else {
                setRamais([]);
            }
        } catch (error) {
            console.error("Erro ao carregar ramais:", error);
            mostrarToast("Erro ao carregar lista de ramais", "error");
        } finally {
            setLoading(false);
        }
    };

    // Buscar lista de usuários do sistema para vinculação
    const carregarUsuarios = async () => {
        try {
            const headers = {
                'Authorization': 'Bearer ' + Cookies.get("token"),
                'Content-Type': 'application/json'
            };
            const response = await api.get('/v1/protected/user/listartodos', { headers });
            if (response.data && Array.isArray(response.data)) {
                setUsuarios(response.data);
            }
        } catch (error) {
            console.error("Erro ao carregar usuários:", error);
        }
    };

    useEffect(() => {
        carregarRamais();
        carregarUsuarios();
    }, []);

    // Ação: Salvar Novo Ramal
    const handleSalvarRamal = async (e) => {
        if (e) e.preventDefault();

        const ramalTrimmed = (novoRamal || '').trim();
        if (!ramalTrimmed) {
            mostrarToast("Por favor, informe o número ou identificador do ramal.", "warning");
            return;
        }

        try {
            setSalvando(true);
            const headers = {
                'Authorization': 'Bearer ' + Cookies.get("token"),
                'Content-Type': 'application/json'
            };

            const payload = {
                ramal: ramalTrimmed,
                nome_mapa: novoNomeMapa ? novoNomeMapa.trim() : `Ramal ${ramalTrimmed}`,
                localizacao: novaLocalizacao ? novaLocalizacao.trim() : '',
                id_usuario: idUsuarioSelecionado ? parseInt(idUsuarioSelecionado) : null
            };

            const res = await api.post('/v1/protected/equipamentos/salvar-ramal', payload, { headers });
            let msg = "Ramal salvo com sucesso!";
            if (typeof res.data === 'string') {
                msg = res.data;
            } else if (res.data && typeof res.data.message === 'string') {
                msg = res.data.message;
            }
            mostrarToast(msg, "success");

            // Limpa formulário e fecha modal
            setNovoRamal('');
            setNovoNomeMapa('');
            setNovaLocalizacao('');
            setIdUsuarioSelecionado('');
            setOpenModal(false);

            // Recarrega listagem
            await carregarRamais();
        } catch (error) {
            console.error("Erro ao salvar ramal:", error);
            let msg = "Erro ao salvar ramal";
            if (error.response?.data) {
                if (typeof error.response.data === 'string') {
                    msg = error.response.data;
                } else if (typeof error.response.data.message === 'string') {
                    msg = error.response.data.message;
                }
            } else if (error.message) {
                msg = error.message;
            }
            mostrarToast(msg, "error");
        } finally {
            setSalvando(false);
        }
    };

    // Ação: Confirmar Exclusão
    const handleConfirmarExcluir = async () => {
        if (!itemExcluir || !itemExcluir.id_equipamento) {
            setOpenModalExcluir(false);
            return;
        }

        try {
            setExcluindo(true);
            const headers = {
                'Authorization': 'Bearer ' + Cookies.get("token"),
                'Content-Type': 'application/json'
            };

            const res = await api.delete(`/v1/protected/equipamentos/excluir-ramal/${itemExcluir.id_equipamento}`, { headers });
            let msg = "Ramal excluído com sucesso!";
            if (typeof res.data === 'string') {
                msg = res.data;
            } else if (res.data && typeof res.data.message === 'string') {
                msg = res.data.message;
            }
            mostrarToast(msg, "success");

            setOpenModalExcluir(false);
            setItemExcluir(null);
            await carregarRamais();
        } catch (error) {
            console.error("Erro ao excluir ramal:", error);
            let msg = "Erro ao excluir ramal";
            if (error.response?.data) {
                if (typeof error.response.data === 'string') {
                    msg = error.response.data;
                } else if (typeof error.response.data.message === 'string') {
                    msg = error.response.data.message;
                }
            } else if (error.message) {
                msg = error.message;
            }
            mostrarToast(msg, "error");
        } finally {
            setExcluindo(false);
        }
    };

    // Filtro de ramais
    const ramaisFiltrados = useMemo(() => {
        if (!searchTerm) return ramais;
        const term = searchTerm.toLowerCase();
        return ramais.filter(r => {
            const ramalVal = (r.ramal || '').toLowerCase();
            const nomeMapaVal = (r.nome_mapa || '').toLowerCase();
            const instalacaoVal = (r.nome_instalacao || '').toLowerCase();
            const usuarioVal = (r.nome_usuario || '').toLowerCase();
            const loginVal = (r.login_usuario || '').toLowerCase();
            const localVal = (r.localizacao || '').toLowerCase();

            return ramalVal.includes(term) ||
                nomeMapaVal.includes(term) ||
                instalacaoVal.includes(term) ||
                usuarioVal.includes(term) ||
                loginVal.includes(term) ||
                localVal.includes(term);
        });
    }, [ramais, searchTerm]);

    // Estatísticas dos ramais
    const stats = useMemo(() => {
        const total = ramais.length;
        const vinculadosEquip = ramais.filter(r => r.nome_instalacao && r.nome_instalacao !== 'Geral' && r.nome_instalacao !== 'Avulso').length;
        const livres = total - vinculadosEquip;
        const vinculadosUsuario = ramais.filter(r => r.nome_usuario || r.id_usuario).length;
        return { total, vinculadosEquip, livres, vinculadosUsuario };
    }, [ramais]);

    // Cores Dark / Light Mode
    const bgMain = darkMode ? '#0f172a' : '#f8fafc';
    const bgCard = darkMode ? '#1e293b' : '#ffffff';
    const borderCard = darkMode ? '#334155' : '#e2e8f0';
    const textPrimary = darkMode ? '#f8fafc' : '#1e293b';
    const textSecondary = darkMode ? '#94a3b8' : '#64748b';
    const rowHoverBg = darkMode ? '#273549' : '#f1f5f9';

    return (
        <div style={{ backgroundColor: bgMain, minHeight: '100vh', transition: 'background-color 0.3s' }}>
            <NavBarGestao />

            <Container maxWidth="xl" style={{ paddingTop: 28, paddingBottom: 48 }}>
                {/* Header da Página */}
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={3}>
                    <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 0 }}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: 12,
                                backgroundColor: darkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
                                color: '#10b981',
                                marginRight: 16
                            }}
                        >
                            <ContactPhoneIcon style={{ fontSize: 28 }} />
                        </Box>
                        <div>
                            <Typography variant="h5" style={{ fontWeight: 700, color: textPrimary, letterSpacing: -0.5 }}>
                                Telefonia e Ramais
                            </Typography>
                            <Typography variant="body2" style={{ color: textSecondary }}>
                                Gerenciamento de ramais VoIP, integrações com equipamentos e vínculos de usuários
                            </Typography>
                        </div>
                    </Box>

                    <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                        <Button
                            variant="outlined"
                            onClick={carregarRamais}
                            disabled={loading}
                            startIcon={<RefreshIcon />}
                            style={{
                                color: textPrimary,
                                borderColor: borderCard,
                                borderRadius: 8,
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            Atualizar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setOpenModal(true)}
                            startIcon={<AddIcCallIcon />}
                            style={{
                                backgroundColor: '#10b981',
                                color: '#ffffff',
                                borderRadius: 8,
                                textTransform: 'none',
                                fontWeight: 700,
                                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
                                padding: '8px 20px'
                            }}
                        >
                            Novo Ramal
                        </Button>
                    </Box>
                </Box>

                {/* Cards de Métricas / Estatísticas */}
                <Grid container spacing={2} style={{ marginBottom: 24 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={0}
                            style={{
                                padding: '16px 20px',
                                backgroundColor: bgCard,
                                border: `1px solid ${borderCard}`,
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                    color: '#10b981',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: 16
                                }}
                            >
                                <CallIcon style={{ fontSize: 24 }} />
                            </Box>
                            <div>
                                <Typography variant="caption" style={{ color: textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
                                    Total de Ramais
                                </Typography>
                                <Typography variant="h5" style={{ color: textPrimary, fontWeight: 800 }}>
                                    {stats.total}
                                </Typography>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={0}
                            style={{
                                padding: '16px 20px',
                                backgroundColor: bgCard,
                                border: `1px solid ${borderCard}`,
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(14, 165, 233, 0.15)',
                                    color: '#0ea5e9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: 16
                                }}
                            >
                                <DevicesIcon style={{ fontSize: 24 }} />
                            </Box>
                            <div>
                                <Typography variant="caption" style={{ color: textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
                                    Em Equipamentos
                                </Typography>
                                <Typography variant="h5" style={{ color: textPrimary, fontWeight: 800 }}>
                                    {stats.vinculadosEquip}
                                </Typography>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={0}
                            style={{
                                padding: '16px 20px',
                                backgroundColor: bgCard,
                                border: `1px solid ${borderCard}`,
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(139, 92, 246, 0.15)',
                                    color: '#8b5cf6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: 16
                                }}
                            >
                                <PhoneInTalkIcon style={{ fontSize: 24 }} />
                            </Box>
                            <div>
                                <Typography variant="caption" style={{ color: textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
                                    Ramais Livres / Avulsos
                                </Typography>
                                <Typography variant="h5" style={{ color: textPrimary, fontWeight: 800 }}>
                                    {stats.livres}
                                </Typography>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={0}
                            style={{
                                padding: '16px 20px',
                                backgroundColor: bgCard,
                                border: `1px solid ${borderCard}`,
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                                    color: '#f59e0b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: 16
                                }}
                            >
                                <PersonIcon style={{ fontSize: 24 }} />
                            </Box>
                            <div>
                                <Typography variant="caption" style={{ color: textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
                                    Com Usuário Associado
                                </Typography>
                                <Typography variant="h5" style={{ color: textPrimary, fontWeight: 800 }}>
                                    {stats.vinculadosUsuario}
                                </Typography>
                            </div>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Filtro e Tabela */}
                <Paper
                    elevation={0}
                    style={{
                        backgroundColor: bgCard,
                        border: `1px solid ${borderCard}`,
                        borderRadius: 14,
                        overflow: 'hidden'
                    }}
                >
                    {/* Barra de Pesquisa */}
                    <Box p={2.5} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ borderBottom: `1px solid ${borderCard}`, gap: 16 }}>
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Buscar por ramal, identificação, instalação ou usuário..."
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value);
                                setPage(0);
                            }}
                            style={{ minWidth: 320, flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ color: textSecondary }} />
                                    </InputAdornment>
                                ),
                                style: {
                                    backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
                                    color: textPrimary,
                                    borderRadius: 8
                                }
                            }}
                        />
                        <Typography variant="body2" style={{ color: textSecondary, fontWeight: 500 }}>
                            Exibindo {ramaisFiltrados.length} de {ramais.length} ramais
                        </Typography>
                    </Box>

                    {/* Tabela de Ramais */}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow style={{ backgroundColor: darkMode ? '#182234' : '#f1f5f9' }}>
                                    <TableCell style={{ color: textSecondary, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ramal</TableCell>
                                    <TableCell style={{ color: textSecondary, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Identificação / Nome</TableCell>
                                    <TableCell style={{ color: textSecondary, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Instalação</TableCell>
                                    <TableCell style={{ color: textSecondary, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Usuário do Sistema</TableCell>
                                    <TableCell style={{ color: textSecondary, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Localização</TableCell>
                                    <TableCell align="right" style={{ color: textSecondary, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" style={{ padding: '40px 0' }}>
                                            <CircularProgress size={32} style={{ color: '#10b981' }} />
                                            <Typography variant="body2" style={{ color: textSecondary, marginTop: 12 }}>
                                                Carregando ramais...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : ramaisFiltrados.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" style={{ padding: '40px 0' }}>
                                            <CallIcon style={{ fontSize: 44, color: textSecondary, opacity: 0.4 }} />
                                            <Typography variant="body1" style={{ color: textPrimary, fontWeight: 600, marginTop: 8 }}>
                                                Nenhum ramal encontrado
                                            </Typography>
                                            <Typography variant="body2" style={{ color: textSecondary }}>
                                                {searchTerm ? 'Tente ajustar os termos da pesquisa.' : 'Clique no botão "Novo Ramal" acima para cadastrar.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    ramaisFiltrados
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((r, index) => {
                                            const isGeral = !r.nome_instalacao || r.nome_instalacao === 'Geral' || r.nome_instalacao === 'Avulso';
                                            return (
                                                <TableRow
                                                    key={r.id_equipamento || `ramal-${r.ramal}-${index}`}
                                                    hover
                                                    style={{
                                                        transition: 'background-color 0.2s',
                                                        borderBottom: `1px solid ${borderCard}`
                                                    }}
                                                >
                                                    {/* Ramal */}
                                                    <TableCell>
                                                        <Chip
                                                            icon={<CallIcon style={{ color: '#ffffff', fontSize: 16 }} />}
                                                            label={<span style={{ fontWeight: 800, fontSize: '0.9rem' }}>{r.ramal}</span>}
                                                            style={{
                                                                backgroundColor: '#10b981',
                                                                color: '#ffffff',
                                                                fontWeight: 700,
                                                                borderRadius: 8,
                                                                paddingLeft: 4
                                                            }}
                                                        />
                                                    </TableCell>

                                                    {/* Identificação */}
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center">
                                                            <Typography variant="body2" style={{ color: textPrimary, fontWeight: 600 }}>
                                                                {r.nome_mapa || `Ramal ${r.ramal}`}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    {/* Instalação */}
                                                    <TableCell>
                                                        {isGeral ? (
                                                            <Chip
                                                                size="small"
                                                                label="Ramal Livre / Avulso"
                                                                style={{
                                                                    backgroundColor: darkMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
                                                                    color: '#8b5cf6',
                                                                    fontWeight: 600,
                                                                    borderRadius: 6
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box display="flex" alignItems="center">
                                                                <BusinessIcon style={{ fontSize: 16, color: '#0ea5e9', marginRight: 6 }} />
                                                                <Typography variant="body2" style={{ color: textPrimary, fontWeight: 500 }}>
                                                                    {r.nome_instalacao}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </TableCell>

                                                    {/* Usuário Associado */}
                                                    <TableCell>
                                                        {r.nome_usuario ? (
                                                            <Chip
                                                                size="small"
                                                                icon={<PersonIcon style={{ color: '#f59e0b', fontSize: 15 }} />}
                                                                label={`${r.nome_usuario}${r.login_usuario ? ` (${r.login_usuario})` : ''}`}
                                                                style={{
                                                                    backgroundColor: darkMode ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)',
                                                                    color: darkMode ? '#fbbf24' : '#b45309',
                                                                    fontWeight: 600,
                                                                    borderRadius: 6
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography variant="caption" style={{ color: textSecondary, fontStyle: 'italic' }}>
                                                                Não associado
                                                            </Typography>
                                                        )}
                                                    </TableCell>

                                                    {/* Localização */}
                                                    <TableCell>
                                                        {r.localizacao ? (
                                                            <Box display="flex" alignItems="center">
                                                                <RoomIcon style={{ fontSize: 16, color: textSecondary, marginRight: 4 }} />
                                                                <Typography variant="body2" style={{ color: textSecondary }}>
                                                                    {r.localizacao}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" style={{ color: textSecondary }}>
                                                                —
                                                            </Typography>
                                                        )}
                                                    </TableCell>

                                                    {/* Ações */}
                                                    <TableCell align="right">
                                                        <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 4 }}>
                                                            {r.id_equipamento && (
                                                                <Tooltip title="Excluir Ramal">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setItemExcluir(r);
                                                                            setOpenModalExcluir(true);
                                                                        }}
                                                                        style={{ color: '#ef4444' }}
                                                                    >
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Paginação */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={ramaisFiltrados.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        labelRowsPerPage="Linhas por página:"
                        style={{ color: textPrimary, borderTop: `1px solid ${borderCard}` }}
                    />
                </Paper>
            </Container>

            {/* Modal: Novo Ramal */}
            <Dialog
                open={openModal}
                onClose={() => !salvando && setOpenModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: bgCard,
                        color: textPrimary,
                        borderRadius: 14,
                        border: `1px solid ${borderCard}`
                    }
                }}
            >
                <form onSubmit={handleSalvarRamal}>
                    <DialogTitle style={{ borderBottom: `1px solid ${borderCard}` }}>
                        <Box display="flex" alignItems="center">
                            <Box
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                    color: '#10b981',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: 12
                                }}
                            >
                                <AddIcCallIcon fontSize="small" />
                            </Box>
                            <div>
                                <Typography variant="h6" style={{ fontWeight: 700, color: textPrimary }}>
                                    Cadastrar Novo Ramal
                                </Typography>
                                <Typography variant="caption" style={{ color: textSecondary }}>
                                    Crie um ramal livre aceitando números, letras e quaisquer caracteres
                                </Typography>
                            </div>
                        </Box>
                    </DialogTitle>

                    <DialogContent style={{ paddingTop: 20 }}>
                        <Grid container spacing={2}>
                            {/* Campo de Ramal Livre */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    required
                                    autoFocus
                                    label="Número / Identificador do Ramal"
                                    placeholder="Ex: 1001, 205A, SUPORTE, PORTARIA-01..."
                                    value={novoRamal}
                                    onChange={e => setNovoRamal(e.target.value)}
                                    helperText="Campo livre: aceita números, letras e caracteres especiais."
                                    InputLabelProps={{ style: { color: textSecondary } }}
                                    InputProps={{
                                        style: {
                                            backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
                                            color: textPrimary,
                                            fontWeight: 700,
                                            borderRadius: 8
                                        },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CallIcon style={{ color: '#10b981' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    FormHelperTextProps={{ style: { color: textSecondary } }}
                                />
                            </Grid>

                            {/* Identificação / Nome Amigável */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Identificação / Nome Amigável"
                                    placeholder="Ex: Portaria Principal, Suporte N2, Direção..."
                                    value={novoNomeMapa}
                                    onChange={e => setNovoNomeMapa(e.target.value)}
                                    InputLabelProps={{ style: { color: textSecondary } }}
                                    InputProps={{
                                        style: {
                                            backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
                                            color: textPrimary,
                                            borderRadius: 8
                                        }
                                    }}
                                />
                            </Grid>

                            {/* Localização */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Localização / Setor"
                                    placeholder="Ex: Bloco A, Guarita 02, Central de Monitoramento..."
                                    value={novaLocalizacao}
                                    onChange={e => setNovaLocalizacao(e.target.value)}
                                    InputLabelProps={{ style: { color: textSecondary } }}
                                    InputProps={{
                                        style: {
                                            backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
                                            color: textPrimary,
                                            borderRadius: 8
                                        },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <RoomIcon style={{ color: textSecondary }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            {/* Associação com Usuário do Sistema */}
                            <Grid item xs={12}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="select-usuario-label" style={{ color: textSecondary }}>
                                        Associar a um Usuário do Sistema (Opcional)
                                    </InputLabel>
                                    <Select
                                        labelId="select-usuario-label"
                                        value={idUsuarioSelecionado}
                                        onChange={e => setIdUsuarioSelecionado(e.target.value)}
                                        label="Associar a um Usuário do Sistema (Opcional)"
                                        style={{
                                            backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
                                            color: textPrimary,
                                            borderRadius: 8
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    backgroundColor: bgCard,
                                                    color: textPrimary,
                                                    maxHeight: 300
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Nenhum / Não associar a usuário</em>
                                        </MenuItem>
                                        {usuarios.map(u => (
                                            <MenuItem key={u.id_usuario} value={u.id_usuario}>
                                                <Box display="flex" alignItems="center">
                                                    <PersonIcon style={{ fontSize: 18, color: '#f59e0b', marginRight: 8 }} />
                                                    <span>{u.nome_amigavel || u.login} {u.nome_amigavel && u.login ? `(${u.login})` : ''}</span>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions style={{ padding: '16px 24px', borderTop: `1px solid ${borderCard}` }}>
                        <Button
                            onClick={() => setOpenModal(false)}
                            disabled={salvando}
                            style={{ color: textSecondary, textTransform: 'none', fontWeight: 600 }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={salvando}
                            startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : <CallIcon />}
                            style={{
                                backgroundColor: '#10b981',
                                color: '#ffffff',
                                borderRadius: 8,
                                textTransform: 'none',
                                fontWeight: 700,
                                padding: '8px 24px'
                            }}
                        >
                            {salvando ? 'Salvando...' : 'Salvar Ramal'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Modal: Confirmar Exclusão */}
            <Dialog
                open={openModalExcluir}
                onClose={() => !excluindo && setOpenModalExcluir(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: bgCard,
                        color: textPrimary,
                        borderRadius: 14,
                        border: `1px solid ${borderCard}`
                    }
                }}
            >
                <DialogTitle>
                    <Typography variant="h6" style={{ fontWeight: 700, color: '#ef4444' }}>
                        Excluir Ramal
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ color: textPrimary }}>
                        Deseja realmente excluir o ramal <strong>{itemExcluir?.ramal}</strong> ({itemExcluir?.nome_mapa})?
                    </Typography>
                    <Typography variant="caption" style={{ color: textSecondary, display: 'block', marginTop: 8 }}>
                        Esta ação removerá o ramal da base de dados do sistema.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: '16px 20px', borderTop: `1px solid ${borderCard}` }}>
                    <Button
                        onClick={() => setOpenModalExcluir(false)}
                        disabled={excluindo}
                        style={{ color: textSecondary, textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmarExcluir}
                        variant="contained"
                        disabled={excluindo}
                        style={{
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 700
                        }}
                    >
                        {excluindo ? 'Excluindo...' : 'Sim, Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toast / Snackbar de Notificação */}
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={fecharToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={fecharToast} severity={toast.severity} elevation={6} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
